import { useEffect } from "react";

export const SITE_URL = "https://solinopro.fr";

function upsertMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export function Seo({ title, description, path = "/", jsonld }) {
    useEffect(() => {
        document.title = title;
        upsertMeta("name", "description", description);
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:type", "website");
        upsertMeta("property", "og:url", SITE_URL + path);
        upsertMeta("property", "og:locale", "fr_FR");
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertLink("canonical", SITE_URL + path);
        document.documentElement.lang = "fr";

        document.querySelectorAll("script[data-seo-page]").forEach((n) => n.remove());
        const blocks = Array.isArray(jsonld) ? jsonld : jsonld ? [jsonld] : [];
        blocks.forEach((block) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-seo-page", "true");
            s.text = JSON.stringify(block);
            document.head.appendChild(s);
        });
    }, [title, description, path, jsonld]);
    return null;
}

// Données réelles visibles sur la fiche Google publique (widget Maps, 12 Rue Brachedal)
// — utilisées tant que l'API Places n'est pas branchée ; l'API prendra le relais automatiquement.
const FALLBACK_AGGREGATE = { ratingValue: "5", reviewCount: "2" };

export function localBusinessLD(aggregate = null) {
    const ld = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": SITE_URL + "/#business",
        name: "Solino Pro",
        description:
            "Artisan peintre et revêtement de sol à Chaumont-en-Vexin : peinture intérieure et extérieure, pose de parquet, sols stratifiés et vinyles, carrelage, habillage d'escaliers.",
        url: SITE_URL,
        telephone: "+33650088282",
        email: "contact@solinopro.fr",
        taxID: "81990716300029",
        sameAs: ["https://instagram.com/solinopro"],
        priceRange: "€€",
        image: SITE_URL + "/favicon.svg",
        address: {
            "@type": "PostalAddress",
            streetAddress: "12 Rue Brachedal",
            postalCode: "60240",
            addressLocality: "Chaumont-en-Vexin",
            addressRegion: "Oise",
            addressCountry: "FR",
        },
        geo: { "@type": "GeoCoordinates", latitude: 49.2667, longitude: 1.8833 },
        areaServed: [
            "Chaumont-en-Vexin", "Gisors", "Méru", "Marines", "Magny-en-Vexin",
            "Chars", "Beauvais", "Pontoise", "Vexin français", "Oise",
        ],
        openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00", closes: "19:00",
        }],
    };
    if ((aggregate || FALLBACK_AGGREGATE) && (aggregate || FALLBACK_AGGREGATE).ratingValue) {
        ld.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: String((aggregate || FALLBACK_AGGREGATE).ratingValue),
            reviewCount: String((aggregate || FALLBACK_AGGREGATE).reviewCount),
        };
    }
    return ld;
}

export function faqLD(faq) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };
}

export function serviceLD({ name, description, price }) {
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        name,
        description,
        provider: { "@id": SITE_URL + "/#business" },
        areaServed: { "@type": "City", name: "Chaumont-en-Vexin" },
        offers: { "@type": "Offer", priceCurrency: "EUR", description: price },
    };
}

export function articleLD({ title, description, slug, date }) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        datePublished: date,
        inLanguage: "fr-FR",
        mainEntityOfPage: SITE_URL + "/blog/" + slug,
        author: { "@id": SITE_URL + "/#business" },
        publisher: { "@id": SITE_URL + "/#business" },
    };
}

export function breadcrumbLD(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: SITE_URL + it.path,
        })),
    };
}
